import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Shield, Plus, Search, Edit, Trash2, Check, Users, Settings, Package, Ticket, Wrench, FileText, Database, Lock, Calendar, BarChart3, Thermometer, Droplets, ClipboardList, Monitor, Truck, Building2, Clock } from 'lucide-react';

// Default system roles
const DEFAULT_ROLES = [
  { name: 'Administrator', code: 'admin', description: 'Acces complet la toate modulele', is_system: true },
  { name: 'Manager', code: 'manager', description: 'Gestioneaza echipa si aproba cereri', is_system: true },
  { name: 'Sef Sectie', code: 'section_chief', description: 'Acces la Portal Sef Sectie - vizualizare si raportare', is_system: true },
  { name: 'Auditor', code: 'auditor', description: 'Acces la Portal Audit - verificare echipamente si mentenanta preventiva', is_system: true },
  { name: 'Tehnician', code: 'technician', description: 'Executa interventii si mentenanta', is_system: true },
  { name: 'Operator', code: 'operator', description: 'Acces de baza - vizualizare si raportare probleme', is_system: true },
  { name: 'Aprovizionare', code: 'procurement', description: 'Gestioneaza stocuri si comenzi', is_system: true },
  { name: 'Vizualizare', code: 'viewer', description: 'Acces doar in citire la dashboarduri si rapoarte', is_system: true }
];

// Permission groups - covers ALL modules
const PERMISSION_GROUPS = [
  {
    name: 'Utilizatori',
    icon: Users,
    color: 'bg-blue-100 text-blue-600',
    permissions: [
      { key: 'users.read', label: 'Vizualizare utilizatori' },
      { key: 'users.write', label: 'Creare/Editare utilizatori' },
      { key: 'users.delete', label: 'Stergere utilizatori' }
    ]
  },
  {
    name: 'Echipamente',
    icon: Package,
    color: 'bg-green-100 text-green-600',
    permissions: [
      { key: 'equipment.read', label: 'Vizualizare echipamente' },
      { key: 'equipment.write', label: 'Creare/Editare echipamente' },
      { key: 'equipment.delete', label: 'Stergere echipamente' },
      { key: 'equipment.passport', label: 'Acces pasaport echipament' },
      { key: 'equipment.bulk', label: 'Actiuni in masa echipamente' }
    ]
  },
  {
    name: 'Tichete',
    icon: Ticket,
    color: 'bg-orange-100 text-orange-600',
    permissions: [
      { key: 'tickets.read', label: 'Vizualizare tichete' },
      { key: 'tickets.write', label: 'Creare/Editare tichete' },
      { key: 'tickets.assign', label: 'Asignare/Redirect tichete' },
      { key: 'tickets.close', label: 'Inchidere tichete cu semnatura' },
      { key: 'tickets.delete', label: 'Stergere tichete' }
    ]
  },
  {
    name: 'Revizii/Interventii',
    icon: Wrench,
    color: 'bg-purple-100 text-purple-600',
    permissions: [
      { key: 'revisions.read', label: 'Vizualizare revizii' },
      { key: 'revisions.write', label: 'Creare/Editare revizii' },
      { key: 'revisions.execute', label: 'Executie interventii' },
      { key: 'revisions.delete', label: 'Stergere revizii' }
    ]
  },
  {
    name: 'Stoc/WMS',
    icon: Database,
    color: 'bg-cyan-100 text-cyan-600',
    permissions: [
      { key: 'stock.read', label: 'Vizualizare stoc' },
      { key: 'stock.write', label: 'Modificare stoc' },
      { key: 'stock.movement', label: 'Transfer/Miscari stoc' },
      { key: 'stock.approve', label: 'Aprobare cereri materiale' },
      { key: 'stock.scanner', label: 'Acces scanner WMS' },
      { key: 'stock.labels', label: 'Generare etichete' }
    ]
  },
  {
    name: 'Scule de Lucru',
    icon: Wrench,
    color: 'bg-teal-100 text-teal-600',
    permissions: [
      { key: 'tools.read', label: 'Vizualizare scule' },
      { key: 'tools.write', label: 'Creare/Editare scule' },
      { key: 'tools.delete', label: 'Stergere scule' },
      { key: 'tools.bulk', label: 'Actiuni in masa scule' }
    ]
  },
  {
    name: 'Furnizori',
    icon: Truck,
    color: 'bg-lime-100 text-lime-600',
    permissions: [
      { key: 'suppliers.read', label: 'Vizualizare furnizori' },
      { key: 'suppliers.write', label: 'Creare/Editare furnizori' },
      { key: 'suppliers.delete', label: 'Stergere furnizori' }
    ]
  },
  {
    name: 'Cereri Materiale',
    icon: ClipboardList,
    color: 'bg-pink-100 text-pink-600',
    permissions: [
      { key: 'material_requests.read', label: 'Vizualizare cereri' },
      { key: 'material_requests.write', label: 'Creare cereri materiale' },
      { key: 'material_requests.approve', label: 'Aprobare/Respingere cereri' }
    ]
  },
  {
    name: 'Rapoarte',
    icon: FileText,
    color: 'bg-amber-100 text-amber-600',
    permissions: [
      { key: 'reports.read', label: 'Vizualizare rapoarte' },
      { key: 'reports.export', label: 'Export rapoarte' },
      { key: 'reports.builder', label: 'Acces Report Builder' },
      { key: 'reports.pdf', label: 'Generare rapoarte PDF' }
    ]
  },
  {
    name: 'Dashboard & Analytics',
    icon: BarChart3,
    color: 'bg-violet-100 text-violet-600',
    permissions: [
      { key: 'dashboard.read', label: 'Vizualizare dashboard' },
      { key: 'dashboard.executive', label: 'Dashboard executiv' },
      { key: 'analytics.read', label: 'Acces analytics avansate' },
      { key: 'analytics.forecast', label: 'Previziuni inventar' }
    ]
  },
  {
    name: 'Calendar & Planificare',
    icon: Calendar,
    color: 'bg-sky-100 text-sky-600',
    permissions: [
      { key: 'calendar.read', label: 'Vizualizare calendar' },
      { key: 'calendar.write', label: 'Modificare calendar' }
    ]
  },
  {
    name: 'Schimburi Tehnicieni',
    icon: Clock,
    color: 'bg-fuchsia-100 text-fuchsia-600',
    permissions: [
      { key: 'shifts.read', label: 'Vizualizare schimburi' },
      { key: 'shifts.write', label: 'Gestionare schimburi' },
      { key: 'shifts.reports', label: 'Rapoarte schimburi' }
    ]
  },
  {
    name: 'ISCIR',
    icon: Thermometer,
    color: 'bg-rose-100 text-rose-600',
    permissions: [
      { key: 'iscir.read', label: 'Vizualizare ISCIR' },
      { key: 'iscir.write', label: 'Gestionare ISCIR' },
      { key: 'iscir.categories', label: 'Categorii ISCIR' }
    ]
  },
  {
    name: 'Lubrificare',
    icon: Droplets,
    color: 'bg-yellow-100 text-yellow-600',
    permissions: [
      { key: 'lubrication.read', label: 'Vizualizare lubrificare' },
      { key: 'lubrication.write', label: 'Gestionare lubrificare' }
    ]
  },
  {
    name: 'Kiosk',
    icon: Monitor,
    color: 'bg-gray-100 text-gray-600',
    permissions: [
      { key: 'kiosk.access', label: 'Acces mod kiosk' },
      { key: 'kiosk.manage', label: 'Gestionare dispozitive kiosk' }
    ]
  },
  {
    name: 'Administrare',
    icon: Settings,
    color: 'bg-red-100 text-red-600',
    permissions: [
      { key: 'settings.read', label: 'Vizualizare setari' },
      { key: 'settings.write', label: 'Modificare setari' },
      { key: 'admin.roles', label: 'Gestionare roluri' },
      { key: 'admin.backup', label: 'Backup/Restore' },
      { key: 'admin.audit', label: 'Vizualizare audit log' },
      { key: 'admin.import_export', label: 'Import/Export date' },
      { key: 'admin.full', label: 'Acces administrator complet' }
    ]
  },
  {
    name: 'Sedii & Locatii',
    icon: Building2,
    color: 'bg-emerald-100 text-emerald-600',
    permissions: [
      { key: 'plants.read', label: 'Vizualizare sedii/locatii' },
      { key: 'plants.write', label: 'Gestionare sedii/locatii' }
    ]
  },
  {
    name: 'Portal Sef Sectie',
    icon: Shield,
    color: 'bg-indigo-100 text-indigo-600',
    permissions: [
      { key: 'portal.access', label: 'Acces portal sef sectie' },
      { key: 'portal.view_stats', label: 'Vizualizare statistici sectie' },
      { key: 'portal.view_tickets', label: 'Vizualizare tichete sectie' },
      { key: 'portal.view_equipment', label: 'Vizualizare echipamente sectie' },
      { key: 'portal.create_tickets', label: 'Creare tichete pentru operatori' },
      { key: 'portal.export', label: 'Export rapoarte sectie' }
    ]
  }
];

const RolesPage = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  
  const [formData, setFormData] = useState({ name: '', description: '', permissions: [], is_system: false });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/roles');
      let rolesData = response.data;
      
      // Add default roles if they don't exist
      const existingCodes = rolesData.map(r => r.code || r.name?.toLowerCase());
      DEFAULT_ROLES.forEach(defaultRole => {
        if (!existingCodes.includes(defaultRole.code)) {
          rolesData.push({ ...defaultRole, id: defaultRole.code, permissions: getDefaultPermissions(defaultRole.code) });
        }
      });
      
      setRoles(rolesData);
      if (rolesData.length > 0 && !selectedRole) {
        setSelectedRole(rolesData[0]);
      }
    } catch (error) {
      toast.error('Eroare la incarcarea datelor');
      // Still show default roles
      setRoles(DEFAULT_ROLES.map(r => ({ ...r, id: r.code, permissions: getDefaultPermissions(r.code) })));
    } finally {
      setLoading(false);
    }
  }, [selectedRole]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getDefaultPermissions = (roleCode) => {
    switch (roleCode) {
      case 'admin':
        return PERMISSION_GROUPS.flatMap(g => g.permissions.map(p => p.key));
      case 'manager':
        return ['users.read', 'equipment.read', 'equipment.write', 'equipment.passport', 'equipment.bulk',
                'tickets.read', 'tickets.write', 'tickets.assign', 'tickets.close',
                'revisions.read', 'revisions.write', 'revisions.execute',
                'stock.read', 'stock.write', 'stock.approve', 'stock.movement',
                'tools.read', 'tools.write', 'suppliers.read', 'suppliers.write',
                'material_requests.read', 'material_requests.write', 'material_requests.approve',
                'reports.read', 'reports.export', 'reports.builder', 'reports.pdf',
                'dashboard.read', 'dashboard.executive', 'analytics.read',
                'calendar.read', 'calendar.write', 'shifts.read', 'shifts.write',
                'iscir.read', 'iscir.write', 'lubrication.read', 'lubrication.write',
                'plants.read', 'admin.audit', 'settings.read'];
      case 'technician':
        return ['equipment.read', 'equipment.passport',
                'tickets.read', 'tickets.write', 'tickets.close',
                'revisions.read', 'revisions.execute',
                'stock.read', 'stock.scanner',
                'tools.read',
                'material_requests.read', 'material_requests.write',
                'dashboard.read', 'calendar.read', 'shifts.read',
                'lubrication.read', 'lubrication.write'];
      case 'section_chief':
        return ['portal.access', 'portal.view_stats', 'portal.view_tickets', 'portal.view_equipment',
                'portal.create_tickets', 'portal.export',
                'equipment.read', 'tickets.read', 'reports.read', 'dashboard.read'];
      case 'operator':
        return ['tickets.read', 'tickets.write', 'equipment.read', 'dashboard.read'];
      case 'procurement':
        return ['stock.read', 'stock.write', 'stock.movement', 'stock.approve',
                'suppliers.read', 'suppliers.write', 'material_requests.read', 'material_requests.approve',
                'reports.read', 'dashboard.read'];
      case 'auditor':
        return ['equipment.read', 'equipment.passport',
                'revisions.read', 'reports.read', 'dashboard.read',
                'iscir.read', 'lubrication.read'];
      case 'viewer':
        return ['equipment.read', 'tickets.read', 'revisions.read', 'stock.read',
                'reports.read', 'dashboard.read'];
      default:
        return [];
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editItem) {
        await api.put(`/roles/${editItem.id}`, formData);
        toast.success('Rol actualizat!');
      } else {
        await api.post('/roles', formData);
        toast.success('Rol creat!');
      }
      setShowDialog(false);
      setEditItem(null);
      setFormData({ name: '', description: '', permissions: [], is_system: false });
      fetchData();
    } catch (error) {
      toast.error('Eroare la salvare');
    }
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setFormData({ 
      name: item.name, 
      description: item.description || '',
      permissions: item.permissions || [],
      is_system: item.is_system || false,
      code: item.code || null
    });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    const role = roles.find(r => r.id === id);
    if (role?.is_system) {
      toast.error('Nu puteti sterge roluri de sistem');
      return;
    }
    if (!window.confirm('Sigur doriti sa stergeti acest rol?')) return;
    try {
      await api.delete(`/roles/${id}`);
      toast.success('Rol sters!');
      fetchData();
    } catch (error) {
      toast.error('Eroare la stergere');
    }
  };

  const togglePermission = (permission) => {
    if (formData.permissions.includes(permission)) {
      setFormData({ ...formData, permissions: formData.permissions.filter(p => p !== permission) });
    } else {
      setFormData({ ...formData, permissions: [...formData.permissions, permission] });
    }
  };

  const toggleGroupPermissions = (group) => {
    const groupKeys = group.permissions.map(p => p.key);
    const allSelected = groupKeys.every(key => formData.permissions.includes(key));
    
    if (allSelected) {
      setFormData({ ...formData, permissions: formData.permissions.filter(p => !groupKeys.includes(p)) });
    } else {
      const newPermissions = [...new Set([...formData.permissions, ...groupKeys])];
      setFormData({ ...formData, permissions: newPermissions });
    }
  };

  const selectAllPermissions = () => {
    const allKeys = PERMISSION_GROUPS.flatMap(g => g.permissions.map(p => p.key));
    setFormData({ ...formData, permissions: allKeys });
  };

  const clearAllPermissions = () => {
    setFormData({ ...formData, permissions: [] });
  };

  const filtered = roles.filter(r => r.name?.toLowerCase().includes(searchTerm.toLowerCase()));

  const getRoleIcon = (role) => {
    if (role.code === 'admin' || role.name?.toLowerCase().includes('admin')) return <Lock className="w-5 h-5" />;
    if (role.code === 'manager' || role.name?.toLowerCase().includes('manager')) return <Users className="w-5 h-5" />;
    if (role.code === 'technician' || role.name?.toLowerCase().includes('tehnic')) return <Wrench className="w-5 h-5" />;
    return <Shield className="w-5 h-5" />;
  };

  return (
    <div className="flex h-[calc(100vh-180px)] gap-6" data-testid="roles-page">
      {/* Left Sidebar - Role List */}
      <div className="w-80 flex-shrink-0 flex flex-col">
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-slate-900">Roluri</h1>
          <p className="text-slate-600 text-sm">Gestioneaza rolurile si permisiunile</p>
        </div>

        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Cauta rol..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 h-10"
          />
        </div>

        <Dialog open={showDialog} onOpenChange={(open) => {
          setShowDialog(open);
          if (!open) { setEditItem(null); setFormData({ name: '', description: '', permissions: [], is_system: false }); }
        }}>
          <DialogTrigger asChild>
            <Button className="btn-primary h-10 mb-4 w-full">
              <Plus className="w-4 h-4 mr-2" />
              ROL NOU
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editItem ? 'Editeaza Rol' : 'Adauga Rol'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nume *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="h-11"
                    placeholder="ex: Supervisor Productie"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Descriere</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Descriere rol..."
                    className="h-11"
                  />
                </div>
              </div>

              <Separator />

              <div className="space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <Label className="text-base font-semibold">Permisiuni</Label>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={selectAllPermissions}>
                      Selecteaza toate
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={clearAllPermissions}>
                      Deselecteaza
                    </Button>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  {PERMISSION_GROUPS.map(group => {
                    const GroupIcon = group.icon;
                    const groupKeys = group.permissions.map(p => p.key);
                    const selectedCount = groupKeys.filter(k => formData.permissions.includes(k)).length;
                    const allSelected = selectedCount === group.permissions.length;
                    
                    return (
                      <div key={group.name} className="border rounded-lg p-3">
                        <div 
                          className="flex items-center justify-between mb-2 cursor-pointer"
                          onClick={() => toggleGroupPermissions(group)}
                        >
                          <div className="flex items-center gap-2">
                            <div className={`w-8 h-8 rounded-lg ${group.color} flex items-center justify-center`}>
                              <GroupIcon className="w-4 h-4" />
                            </div>
                            <span className="font-medium">{group.name}</span>
                          </div>
                          <Badge variant={allSelected ? 'default' : 'outline'} className="text-xs">
                            {selectedCount}/{group.permissions.length}
                          </Badge>
                        </div>
                        <div className="space-y-1 pl-10">
                          {group.permissions.map(perm => (
                            <label key={perm.key} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 p-1 rounded">
                              <input
                                type="checkbox"
                                checked={formData.permissions.includes(perm.key)}
                                onChange={() => togglePermission(perm.key)}
                                className="w-4 h-4 rounded text-[#0052CC]"
                              />
                              <span className="text-slate-600">{perm.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Anuleaza</Button>
                <Button type="submit" className="btn-primary">
                  {editItem ? 'ACTUALIZEAZA' : 'ADAUGA'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Role List */}
        <div className="flex-1 overflow-y-auto space-y-2">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0052CC]"></div>
            </div>
          ) : filtered.length > 0 ? (
            filtered.map(role => (
              <div
                key={role.id}
                onClick={() => setSelectedRole(role)}
                className={`p-3 rounded-lg cursor-pointer transition-all ${
                  selectedRole?.id === role.id 
                    ? 'bg-[#0052CC] text-white' 
                    : 'bg-white hover:bg-slate-50 border'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    selectedRole?.id === role.id ? 'bg-white/20' : 'bg-violet-100 text-violet-600'
                  }`}>
                    {getRoleIcon(role)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold truncate">{role.name}</p>
                    <p className={`text-xs truncate ${selectedRole?.id === role.id ? 'text-white/70' : 'text-slate-500'}`}>
                      {role.permissions?.length || 0} permisiuni
                    </p>
                  </div>
                  {role.is_system && (
                    <Badge variant={selectedRole?.id === role.id ? 'secondary' : 'outline'} className="text-xs">
                      Sistem
                    </Badge>
                  )}
                </div>
              </div>
            ))
          ) : (
            <p className="text-center text-slate-500 py-8">Nu exista roluri</p>
          )}
        </div>
      </div>

      {/* Right Panel - Role Details */}
      <div className="flex-1 bg-white rounded-lg border overflow-y-auto">
        {selectedRole ? (
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-violet-100 flex items-center justify-center text-violet-600">
                  {getRoleIcon(selectedRole)}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900">{selectedRole.name}</h2>
                  <p className="text-slate-500">{selectedRole.description || 'Fara descriere'}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => handleEdit(selectedRole)}>
                  <Edit className="w-4 h-4 mr-2" />
                  Editeaza
                </Button>
                {!selectedRole.is_system && (
                  <Button variant="outline" onClick={() => handleDelete(selectedRole.id)} className="text-red-600 hover:bg-red-50">
                    <Trash2 className="w-4 h-4 mr-2" />
                    Sterge
                  </Button>
                )}
              </div>
            </div>

            <Separator className="my-6" />

            <h3 className="text-lg font-semibold mb-4">Permisiuni Active ({selectedRole.permissions?.length || 0})</h3>
            
            <div className="grid md:grid-cols-2 gap-4">
              {PERMISSION_GROUPS.map(group => {
                const GroupIcon = group.icon;
                const activePerms = group.permissions.filter(p => selectedRole.permissions?.includes(p.key));
                
                if (activePerms.length === 0) return null;
                
                return (
                  <Card key={group.name} className="card-industrial">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-lg ${group.color} flex items-center justify-center`}>
                          <GroupIcon className="w-4 h-4" />
                        </div>
                        {group.name}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-2">
                      <div className="space-y-1">
                        {activePerms.map(perm => (
                          <div key={perm.key} className="flex items-center gap-2 text-sm">
                            <Check className="w-4 h-4 text-green-500" />
                            <span>{perm.label}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {(!selectedRole.permissions || selectedRole.permissions.length === 0) && (
              <div className="text-center py-12 text-slate-500">
                <Shield className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <p>Acest rol nu are permisiuni active</p>
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-slate-500">
            <div className="text-center">
              <Shield className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <p>Selecteaza un rol pentru a vedea detaliile</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RolesPage;
